import { Capacitor } from "@capacitor/core";
import { FirebaseAppCheck } from "@capacitor-firebase/app-check";
import {
  CustomProvider,
  ReCaptchaEnterpriseProvider,
  getToken as getAppCheckToken,
  initializeAppCheck,
} from "firebase/app-check";

let initialization;
let sdkAppCheck;

const APP_CHECK_TIMEOUT_MS = 10000;

const withAppCheckTimeout = (promise, stage) => new Promise((resolve, reject) => {
  const timeoutId = setTimeout(() => {
    const error = new Error(`App Check timed out during ${stage}.`);
    error.code = "app-check/timeout";
    reject(error);
  }, APP_CHECK_TIMEOUT_MS);

  Promise.resolve(promise).then(
    (value) => { clearTimeout(timeoutId); resolve(value); },
    (error) => { clearTimeout(timeoutId); reject(error); },
  );
});

export function initializeZunoAppCheck(app) {
  if (initialization) return initialization;
  initialization = (async () => {
    const platform = Capacitor.getPlatform();
    if (platform === "web") {
      const siteKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY;
      if (!siteKey) {
        if (process.env.NODE_ENV === "production") console.warn("[ZunoSecurity] App Check web aguardando site key.");
        return { enabled: false, reason: "missing-web-site-key" };
      }
      if (!sdkAppCheck) {
        sdkAppCheck = initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(siteKey),
          isTokenAutoRefreshEnabled: false,
        });
      }
      await withAppCheckTimeout(getAppCheckToken(sdkAppCheck, false), "web-token");
      return { enabled: true, provider: "recaptcha-enterprise" };
    }

    // iOS registers the DeviceCheck provider synchronously in AppDelegate,
    // before FirebaseApp.configure(). Calling the plugin initialize() here
    // would replace that factory with App Attest after Firebase is already
    // configured; Android still initializes Play Integrity through the plugin.
    const nativeInitialization = platform === "ios"
      ? Promise.resolve()
      : FirebaseAppCheck.initialize();
    const nativeReady = withAppCheckTimeout(
      nativeInitialization.then(() => FirebaseAppCheck.setTokenAutoRefreshEnabled({ enabled: true })),
      "native-initialization",
    );
    if (!sdkAppCheck) {
      sdkAppCheck = initializeAppCheck(app, {
        provider: new CustomProvider({
          getToken: async () => {
            await nativeReady;
            const result = await withAppCheckTimeout(
              FirebaseAppCheck.getToken({ forceRefresh: false }),
              "native-token",
            );
            return { token: result.token, expireTimeMillis: result.expireTimeMillis || Date.now() + 30 * 60 * 1000 };
          },
        }),
        isTokenAutoRefreshEnabled: false,
      });
    }
    await nativeReady;
    await withAppCheckTimeout(getAppCheckToken(sdkAppCheck, false), "sdk-token");
    return { enabled: true, provider: platform === "android" ? "play-integrity" : "device-check" };
  })().catch((error) => {
    console.error("[ZunoSecurity] App Check initialization failed", {
      code: error?.code || "app-check/init",
      message: error?.message || "App Check initialization failed",
    });
    initialization = undefined;
    return { enabled: false, reason: error?.code || "initialization-failed" };
  });
  return initialization;
}
